import { motion } from "framer-motion";
import { Eyebrow, fadeUp, Chip } from "@/components/landing/shared";
import { BrowserShowcase, PhoneShowcase } from "@/components/landing/Showcase";

const STACK = [
  "React",
  "FastAPI",
  "MongoDB",
  "REST API",
  "Python",
  "Authentication",
  "Mobile-first",
  "Pronto per SDI",
];

const POINTS = [
  {
    label: "Il problema",
    body: "I ristoratori non sanno in tempo reale dove perdono margine. Food cost, personale e costi fissi vivono in fogli separati e calcoli manuali: quando emerge un problema, spesso è già tardi.",
  },
  {
    label: "La soluzione",
    body: "Una dashboard KPI a semaforo che in 5 secondi mostra cosa va bene e dove intervenire, un'analisi delle perdite in euro con potenziale di recupero, un simulatore what-if e l'analisi del food cost per ricetta.",
  },
  {
    label: "L'architettura & i workflow",
    body: "App mobile-first con React e FastAPI, MongoDB e API REST, autenticazione e account demo. Flusso: login → dashboard giornaliera → semaforo KPI con soglie → analisi perdite → azioni consigliate. Architettura predisposta per la fatturazione elettronica (SDI).",
  },
];

export default function CaseStudyTaste() {
  return (
    <section className="relative border-t border-black/[0.08] py-16 md:py-24 lg:py-28">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <Eyebrow>Case study · 02</Eyebrow>
          <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-3xl lg:text-4xl">
            Problemi diversi. Architetture diverse.{" "}
            <span className="text-emerald-600">Lo stesso metodo.</span>
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="font-display text-lg font-semibold text-[color:var(--text-primary)]">
              Taste Margin
            </span>
            <span className="h-4 w-px bg-black/15" />
            <span className="text-sm text-tertiary">
              SaaS per il controllo dei margini nella ristorazione
            </span>
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Screens */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="lg:col-span-7"
          >
            <div className="grid grid-cols-1 items-center gap-6 sm:grid-cols-5">
              <div className="sm:col-span-3">
                <BrowserShowcase
                  src="/projects/chefpro-full.jpg"
                  url="chefpro.it"
                  alt="Taste Margin — anteprima della piattaforma"
                  caption="Anteprima live — food cost, KPI e controllo margini"
                  scrollMs={10000}
                />
              </div>
              <div className="sm:col-span-2">
                <PhoneShowcase
                  src="/projects/chefpro-app-tall.jpg"
                  alt="Taste Margin — app mobile, dashboard KPI"
                  caption="App mobile — dashboard KPI a semaforo"
                  scrollMs={8500}
                />
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-5">
            <div className="space-y-6">
              {POINTS.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-40px" }}
                  variants={fadeUp}
                  custom={i}
                  className="border-l-2 border-emerald-400/50 pl-5"
                >
                  <h3 className="text-base font-semibold text-[color:var(--text-primary)]">
                    {p.label}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-secondary">
                    {p.body}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2.5">
              <span className="mr-1 text-xs uppercase tracking-wide text-tertiary">
                Stack tecnico
              </span>
              {STACK.map((t) => (
                <Chip key={t}>{t}</Chip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
